import ActionButton from "../atoms/ActionButton";

// null/undefined/NaN は 0 として数値変換する
const toNum = (v) => (v == null ? 0 : Number(v));

// YYYYMMDD → YYYY/MM/DD に変換して表示用にする
const formatDateDisplay = (yyyymmdd) =>
  `${yyyymmdd.slice(0, 4)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(6, 8)}`;

// 指定年月の全日付を YYYYMMDD 配列で返す
const getDaysInMonth = (yearMonth) => {
  const year = parseInt(yearMonth.slice(0, 4), 10);
  const month = parseInt(yearMonth.slice(4, 6), 10);
  const lastDay = new Date(year, month, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => {
    const d = String(i + 1).padStart(2, "0");
    return `${year}${String(month).padStart(2, "0")}${d}`;
  });
};

// CSV列定義（全店舗1ファイルに統合・Message列は除外）
// getValue(row, ctx): row はDBデータ（データなし日は null）、ctx は {yyyymmdd, shopNo, shopName, regiNo}
const CSV_COLUMNS = [
  { label: "日付",         getValue: (row, ctx) => formatDateDisplay(ctx.yyyymmdd) },
  { label: "店舗番号",     getValue: (row, ctx) => ctx.shopNo },
  { label: "店舗名",       getValue: (row, ctx) => ctx.shopName },
  { label: "レジ番号",     getValue: (row, ctx) => ctx.regiNo },
  { label: "現金売上",     getValue: (row) => row?.GenkinUriage  ?? "" },
  { label: "掛売上",       getValue: (row) => row?.KakeUriage    ?? "" },
  { label: "その他売上",   getValue: (row) => row?.SonotaUriage  ?? "" },
  { label: "うち消費税",   getValue: (row) => row?.SyohiZei      ?? "" },
  { label: "その他控除",   getValue: (row) => row?.SonotaKoujo   ?? "" },
  { label: "総売上",       getValue: (row) => row?.SoUriage      ?? "" },
  { label: "純売上",       getValue: (row) => row?.JunUriage     ?? "" },
  { label: "客数",         getValue: (row) => row?.CustNo        ?? "" },
  { label: "入金機投入額", getValue: (row) => row?.GenkinAridaka ?? "" },
  { label: "現金相違額",   getValue: (row) => row ? toNum(row.GenkinUriage) - toNum(row.GenkinAridaka) : "" },
  { label: "担当者",       getValue: (row) => row ? (row.UpdatePerson ?? row.InputPerson ?? "") : "" },
];

// セルの値を CSV 安全な文字列に変換する
const escapeCsvCell = (val) => {
  const s = String(val ?? "");
  return s.includes(",") || s.includes("\n") || s.includes('"')
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};

// UTF-8 BOM 付き CSV 文字列を生成する
// entries: [{yyyymmdd, shopNo, shopName, regiNo, data}] の配列（data は DB 行 or null）
const buildCsvContent = (entries) => {
  const header = CSV_COLUMNS.map((c) => c.label).join(",");
  const dataRows = entries.map(({ data, ...ctx }) =>
    CSV_COLUMNS.map((c) => escapeCsvCell(c.getValue(data, ctx))).join(",")
  );
  const bom = "﻿";
  return bom + [header, ...dataRows].join("\n");
};

/**
 * AllShopsCsvDownload - 全店舗一括 CSV ダウンロードボタン
 *
 * 全店舗・全レジのデータを1つのCSVファイルにまとめてダウンロードする。
 * 事務所側の取り込みツールが1クリックで一括取り込みできることを優先し、店舗別ファイル分割は行わない。
 * データのない日付も行として含める（空白部分で定休日等が視覚的に分かるよう）。
 * Message列は共同開発者の変換アプリとの互換性維持のため含めない。
 *
 * @param {object[]} regiInfo  - 全レジ情報 [{ShopNo, ShopName, RegiNo, RegiName}]
 * @param {object[]} reports   - 全店舗月次データ [{YYYYMMDD, ShopNo, RegiNo, ...}]
 * @param {string}   yearMonth - 対象年月 (YYYYMM形式)
 * @param {boolean}  disabled  - ボタン非活性フラグ
 */
function AllShopsCsvDownload({ regiInfo, reports, yearMonth, disabled }) {
  const handleDownload = () => {
    const days = getDaysInMonth(yearMonth);
    // 事務所アカウント（ShopNo=0）は除外
    const regis = regiInfo.filter((r) => r.ShopNo !== 0);

    // 日付 → 店舗番号 → レジ番号 の順で全行を列挙する（データなし日も含む）
    const entries = [];
    for (const yyyymmdd of days) {
      for (const regi of regis) {
        const data = reports.find(
          (r) =>
            r.YYYYMMDD === yyyymmdd &&
            String(r.ShopNo) === String(regi.ShopNo) &&
            String(r.RegiNo) === String(regi.RegiNo)
        ) ?? null;
        entries.push({
          yyyymmdd,
          shopNo: regi.ShopNo,
          shopName: regi.ShopName,
          regiNo: regi.RegiNo,
          data,
        });
      }
    }

    const csvContent = buildCsvContent(entries);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${yearMonth}_全店舗売上データ.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <ActionButton
      label="CSV一括ダウンロード"
      onClick={handleDownload}
      disabled={disabled}
      paddingClass="px-4 py-2"
      textSizeClass="text-base"
    />
  );
}

export default AllShopsCsvDownload;
